import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AppStateFailureBuilder, AppStateFailureItemWithErrorCode, FailureType } from '@symphony-solo/shared/common-state/src';
import { ApiResponseCode } from '@symphony-solo/shared/http/src';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ChooseLocationFacade, ChooseLocationService } from '@symphony-solo/domain/choose-location/src/';
import { AlertService } from '@symphony-solo/shared/alert/src';
import {
  getChooseLocationAction,
  getChooseLocationErrorAction,
  getLocationTimeslotForQrScanAction,
  getLocationTimeslotErrorAction,
  getLocationTimeslotSuccessAction,
} from './scan-qr-order.actions';
import { of } from 'rxjs';
import { CHOOSE_MENU_TABS, DateHelper } from '@symphony-solo/shared/utils/src';
import { NavController } from '@ionic/angular';

@Injectable()
export class ScanQROrderEffects {
  constructor(
    private action$: Actions,
    private chooseLocationService: ChooseLocationService,
    private chooseLocationFacade: ChooseLocationFacade,
    private alertService: AlertService,
    private navCtrl: NavController
  ) {}

  protected getChooseLocation$ = createEffect(() =>
    this.action$.pipe(
      ofType(getChooseLocationAction),
      switchMap((action) => {
        return this.chooseLocationService.getChooseLocation().pipe(
          map((chooseLocation: any) => {
            if (action?.siteId) {
              const locationFound = chooseLocation.find((location) => location.id === action.siteId);
              if (locationFound) {
                this.chooseLocationFacade.setSelectedLocation(locationFound);
                const orderDate = DateHelper.formatDateToYYYYMMDD(new Date());
                return getLocationTimeslotForQrScanAction({ locationID: locationFound.id, orderDate });
              } else {
                this.alertService.presentAlert(
                  `You've missed us!`,
                  'Please check our opening hours and try another time. Thanks',
                  [],
                  false
                );
              }
            } else {
              return getChooseLocationErrorAction(null);
            }
          }),
          catchError((e) => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on getting location', ApiResponseCode.Server_Error))
              .build();
            return of(getChooseLocationErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected getLocationTimeSlot$ = createEffect(() =>
    this.action$.pipe(
      ofType(getLocationTimeslotForQrScanAction),
      switchMap((payload) => {
        return this.chooseLocationService.getLocationTimeslots(payload).pipe(
          map((locationTimeslot: any) => {
            if (locationTimeslot && locationTimeslot.length === 0) {
              this.alertService.presentAlert(`You've missed us!`, 'Please check our opening hours and try another time. Thanks', [], false);
            }

            const period = locationTimeslot[0];
            const currentTimeSlot = period.timeslots.find((timeslot) => timeslot.isOrderNow);

            if (currentTimeSlot) {
              this.chooseLocationFacade.setSelectedTimeslot({
                ...currentTimeSlot,
                periodID: period.periodID,
                timeslotDisplayOption: period.timeslotDisplayOption,
                text: this.timeslotText(currentTimeSlot, period),
              });

              this.navCtrl.navigateForward(CHOOSE_MENU_TABS);
            } else {
              this.alertService.presentAlert(`You've missed us!`, 'Please check our opening hours and try another time. Thanks', [], false);
            }

            return getLocationTimeslotSuccessAction();
          }),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on getting locationTimeslot', ApiResponseCode.Server_Error))
              .build();
            return of(getLocationTimeslotErrorAction({ failure }));
          })
        );
      })
    )
  );

  private timeslotText(t, locationTimeslot): string {
    if (locationTimeslot.timeslotDisplayOption === 'TimeslotTime') {
      return `<span id="${t?.start}">${DateHelper.getLocalDateByHM(t?.start)} - ${DateHelper.getLocalDateByHM(t?.end)}</span>`;
    } else if (locationTimeslot.timeslotDisplayOption === 'TimeslotTimeAndDisplayName') {
      return `<span id="${t?.start}">${DateHelper.getLocalDateByHM(t?.start)} - ${DateHelper.getLocalDateByHM(
        t?.end
      )} <span class="f-bold pl-2">${locationTimeslot.periodName}</span></span>`;
    } else if (locationTimeslot.timeslotDisplayOption === 'DisplayName') {
      return `<span id="${t?.start}"><span class="f-bold pl-2">${locationTimeslot.periodName}</span></span>`;
    }
  }
}
